import { useRef, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Modal,
  Backdrop,
  Fade,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloseIcon from "@mui/icons-material/Close";
import { UPLOAD_FILE_ENDPOINT } from "../config";

interface UploadModalProps {
  open: boolean;
  onClose: () => void;
  onUpload: (file: File, requestId: string, responseData: any) => void;
}
function UploadModal({ open, onClose, onUpload }: UploadModalProps) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleClose = () => {
    setSelectedFile(null);
    setDragActive(false);
    onClose();
  };

  const handleFileUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      console.log("Selected file:", file);
      console.log("File name:", file.name);
      console.log("File size:", file.size);
      console.log("File type:", file.type);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      setSelectedFile(files[0]);
    }
  };

  const handleConfirmUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile); // Only send the file

      const response = await fetch(UPLOAD_FILE_ENDPOINT, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("File upload failed");
      }

      const result = await response.json();
      console.log("File uploaded successfully:", result);

      // Extract request_id and data from response (similar to LicenseDetails)
      const requestId = result.data?.request_id;
      const responseData = result.data;

      if (requestId && responseData) {
        onUpload(selectedFile, requestId, responseData);
        handleClose();
      } else {
        throw new Error("No request_id received from server");
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      setUploadError("Error uploading file");

      handleClose();
    } finally {
      setIsUploading(false);
    }
  };
  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
    >
      <Fade in={open}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isSmallScreen ? "70%" : isMediumScreen ? "70%" : "25%",
            bgcolor: "background.paper",
            borderRadius: 3,
            boxShadow: 24,
            p: isSmallScreen ? 2 : 3,
            outline: "none",
            maxHeight: isSmallScreen
              ? "70vh"
              : isMediumScreen
              ? "50vh"
              : "45vh",
            overflow: "auto",
          }}
        >
          {/* Modal Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: isSmallScreen ? 1.5 : 2,
            }}
          >
            <Typography
              variant={isSmallScreen ? "subtitle1" : "h6"}
              component="h2"
            >
              Upload License Request
            </Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* File Drop Zone */}
          <Box
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            sx={{
              border: dragActive
                ? "2px dashed #2BB132"
                : selectedFile
                ? "2px solid #2BB132"
                : "2px dashed #ccc",
              borderRadius: 2,
              p: isSmallScreen ? 2 : 3,
              textAlign: "center",
              backgroundColor: dragActive
                ? "rgba(43, 177, 50, 0.1)"
                : selectedFile
                ? "rgba(43, 177, 50, 0.05)"
                : "transparent",
              transition: "all 0.3s ease",
              cursor: "pointer",
              mb: isSmallScreen ? 1.5 : 2,
            }}
            onClick={handleFileUpload}
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="image/*"
              style={{ display: "none" }}
            />

            {selectedFile ? (
              <Box>
                <UploadFileIcon
                  sx={{
                    fontSize: isSmallScreen ? 40 : 48,
                    color: "#2BB132",
                    mb: isSmallScreen ? 1.5 : 2,
                  }}
                />
                <Typography
                  variant={isSmallScreen ? "subtitle1" : "h6"}
                  sx={{ color: "#2BB132", mb: 1 }}
                >
                  File Selected!
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedFile.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Size: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </Typography>
              </Box>
            ) : (
              <Box>
                <UploadFileIcon
                  sx={{
                    fontSize: isSmallScreen ? 40 : 48,
                    color: "#ccc",
                    mb: isSmallScreen ? 1.5 : 2,
                  }}
                />
                <Typography
                  variant={isSmallScreen ? "subtitle1" : "h6"}
                  sx={{ mb: 1 }}
                >
                  {dragActive ? "Drop file here" : "Choose or drag file here"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Supports: JPG, PNG, GIF files
                </Typography>
              </Box>
            )}
          </Box>

          {/* Modal Actions */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "flex-end",
            }}
          >
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{
                borderColor: "#ccc",
                color: "#666",
                "&:hover": {
                  borderColor: "#999",
                  backgroundColor: "rgba(0,0,0,0.04)",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmUpload}
              disabled={!selectedFile}
              sx={{
                backgroundColor: "#2BB132",
                "&:hover": {
                  backgroundColor: "#388E3C",
                },
                "&:disabled": {
                  backgroundColor: "#ccc",
                },
              }}
            >
              Upload File
            </Button>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
}

export default UploadModal;
